import { htmlKeepsStructure, plainLinesFromText, quillBlocksFromHtml, shouldPasteAsPlainLines } from './articleFormatting';
import { editorArticleHtml } from './formatArticleContent';
import { sanitizeArticleHtml } from './sanitizeArticleHtml';

let tableBlotRegistered = false;

function ensureArticleFormats(Quill) {
  if (!Quill || tableBlotRegistered) return;
  const BlockEmbed = Quill.import('blots/block/embed');

  class ArticleTableBlot extends BlockEmbed {
    static create(value) {
      const node = super.create();
      node.setAttribute('contenteditable', 'false');
      node.innerHTML = sanitizeArticleHtml(value);
      return node;
    }

    static value(node) {
      const table = node.querySelector('table');
      return table ? table.outerHTML : '';
    }
  }

  ArticleTableBlot.blotName = 'article-table';
  ArticleTableBlot.tagName = 'DIV';
  ArticleTableBlot.className = 'article-table';
  Quill.register(ArticleTableBlot, true);
  tableBlotRegistered = true;
}

function replaceRange(quill) {
  const range = quill.getSelection(true) || { index: 0, length: 0 };
  if (range.length) quill.deleteText(range.index, range.length, 'user');
  return range.index;
}

function insertPlainLines(quill, text) {
  const lines = plainLinesFromText(text);
  if (!lines.length) return;
  const indexStart = replaceRange(quill);
  if (indexStart == null) return;
  let index = indexStart;
  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      quill.insertText(index, '\n', 'user');
      index += 1;
    }
    if (line.length) {
      quill.insertText(index, line, 'user');
      index += line.length;
    }
  });
  quill.setSelection(index, 0, 'silent');
}

function insertHtml(quill, html) {
  const clean = quillBlocksFromHtml(sanitizeArticleHtml(html));
  if (!clean.trim()) return;
  const index = replaceRange(quill);
  if (index == null) return;
  quill.clipboard.dangerouslyPasteHTML(index, clean, 'user');
}

function handlePaste(quill, event) {
  const clipboard = event.clipboardData;
  if (!clipboard) return;
  const html = clipboard.getData('text/html') || '';
  const text = clipboard.getData('text/plain') || '';

  if (shouldPasteAsPlainLines(html, text)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    insertPlainLines(quill, text);
    return;
  }

  if (htmlKeepsStructure(html)) {
    const clean = sanitizeArticleHtml(html);
    if (clean.trim()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      insertHtml(quill, clean);
      return;
    }
  }

  if (!/[\r\n\u2028\u2029]/.test(text)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  insertPlainLines(quill, text);
}

export function prepareArticleQuill(quill) {
  if (!quill || quill.__articlePasteReady) return;
  const Quill = quill.constructor;
  ensureArticleFormats(Quill);
  const Delta = Quill.import('delta');
  quill.clipboard.addMatcher('TABLE', (node) => new Delta().insert({ 'article-table': node.outerHTML }));
  quill.clipboard.addMatcher('DIV.article-table', (node) => {
    const table = node.querySelector('table');
    return new Delta().insert(table ? { 'article-table': table.outerHTML } : '\n');
  });
  quill.root.addEventListener('paste', (event) => handlePaste(quill, event), true);
  quill.__articlePasteReady = true;
}

export function loadArticleHtml(quill, content) {
  if (!quill) return;
  prepareArticleQuill(quill);
  const html = editorArticleHtml(content);
  quill.setText('');
  if (html) quill.clipboard.dangerouslyPasteHTML(html);
}
